import ArticleLayout from "@/components/feature/ArticleLayout";
import { Link } from "react-router-dom";

const faqs = [
  { q: "Muss ich mich mit OOPBUY auskennen, um das Spreadsheet zu nutzen?", a: "Nein, das OOPBUY Spreadsheet ist speziell für Anfänger konzipiert. Alle Kategorien sind selbsterklärend und übersichtlich strukturiert." },
  { q: "Brauche ich ein OOPBUY Konto, um das Spreadsheet zu nutzen?", a: "Das Spreadsheet selbst ist eine Informationsressource und erfordert kein eigenes Konto. Für den eigentlichen Einkauf auf OOPBUY benötigst du allerdings ein Konto." },
  { q: "Kann ich das Spreadsheet auch auf dem Handy nutzen?", a: "Absolut. Das OOPBUY Spreadsheet ist vollständig mobil-optimiert und funktioniert auf allen Smartphones und Tablets einwandfrei." },
];

export default function AnfaengerGuidePage() {
  return (
    <ArticleLayout
      title1="OOPBUY Spreadsheet"
      title2="Anfänger Guide"
      title3="Von Null auf Profi"
      date="11. Juli 2026"
      readTime="13 Minuten"
      tag="Anfänger"
      seoDescription="OOPBUY Spreadsheet Anfänger Guide 2026. Von Null zum Profi — lerne Schritt für Schritt, wie du das OOPBUY Spreadsheet nutzt, typische Anfängerfehler vermeidest und erfolgreich bestellst."
      faqs={faqs}
    >
      <p className="text-lg text-foreground-700 leading-relaxed">
        Du bist neu bei OOPBUY und fragst dich, wo du anfangen sollst? Keine Sorge — dieser <strong>OOPBUY Spreadsheet Anfänger Guide</strong> führt dich Schritt für Schritt durch alles, was du wissen musst. Von der ersten Anmeldung bis zur ersten erfolgreichen Bestellung — wir lassen dich nicht allein.
      </p>

      <div className="my-6">
        <img
          src="https://readdy.ai/api/search-image?query=Fresh%20clean%20white%20desk%20with%20open%20notebook%20and%20laptop%20showing%20organized%20spreadsheet%2C%20morning%20sunlight%20streaming%20through%20window%2C%20beginner%20workspace%20setup%2C%20minimalist%20and%20welcoming%20atmosphere%2C%20soft%20sage%20green%20stationery%20accents%2C%20calm%20organized%20composition%2C%20editorial%20lifestyle%20photography%2C%20warm%20neutral%20tones%2C%20encouraging%20aesthetic&width=1200&height=700&seq=article-anfaenger-desk-v2&orientation=landscape"
          alt="OOPBUY Spreadsheet Anfänger Guide"
          className="w-full rounded-xl object-cover"
        />
      </div>

      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">
        Was ist OOPBUY überhaupt?
      </h2>
      <p className="text-foreground-700 leading-relaxed">
        OOPBUY ist eine internationale Shopping-Plattform, die es dir ermöglicht, Produkte aus verschiedenen Quellen zu entdecken und zu bestellen. Das <strong>OOPBUY Spreadsheet</strong> ist eine von der Community erstellte Ressource, die dir hilft, dich in der Produktvielfalt zurechtzufinden. Es bündelt alle wichtigen Informationen an einem Ort — perfekt für Anfänger.
      </p>

      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">
        Die ersten Schritte: So startest du mit dem Spreadsheet
      </h2>
      <div className="space-y-4 my-6">
        {[
          { step: "1", title: "Mit der Startseite vertraut machen", desc: "Öffne das OOPBUY Spreadsheet und nimm dir ein paar Minuten Zeit, um dich mit dem Layout vertraut zu machen. Die 14 Kategorien sind übersichtlich aufgelistet." },
          { step: "2", title: "Interessen-Kategorien auswählen", desc: "Überlege, welche Produktkategorien dich interessieren. Konzentriere dich zunächst auf 2-3 Kategorien, um nicht überwältigt zu werden." },
          { step: "3", title: "Erste Produkte erkunden", desc: "Klicke dich durch deine ausgewählten Kategorien und mache dich mit den Produktbeschreibungen, Preisen und Bildern vertraut." },
          { step: "4", title: "Wunschliste erstellen", desc: "Im OOPBUY Spreadsheet kannst du dir eine persönliche Wunschliste mit interessanten Produkten zusammenstellen, bevor du deine erste Bestellung aufgibst." },
        ].map((step) => (
          <div key={step.step} className="bg-background-100 rounded-xl p-5 border border-foreground-100/30">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 rounded-full bg-primary-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">{step.step}</div>
              <h3 className="font-heading font-semibold text-foreground-900 text-base">{step.title}</h3>
            </div>
            <p className="text-sm text-foreground-600 ml-11">{step.desc}</p>
          </div>
        ))}
      </div>

      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">
        Die häufigsten Anfängerfehler und wie du sie vermeidest
      </h2>
      <ul className="space-y-3 list-none pl-0 text-foreground-700 my-6">
        {[
          { fehler: "Zu viele Produkte auf einmal bestellen", loesung: "Starte mit einer kleinen Testbestellung von 2-3 Artikeln, um den Prozess kennenzulernen." },
          { fehler: "Produktbeschreibungen überspringen", loesung: "Lies jede Produktbeschreibung sorgfältig. Das OOPBUY Spreadsheet enthält wichtige Details, die du nicht übersehen solltest." },
          { fehler: "QC-Bilder ignorieren", loesung: "Fordere immer QC-Bilder an, bevor deine Bestellung versendet wird. So vermeidest du Überraschungen." },
          { fehler: "Keine Preisvergleiche durchführen", loesung: "Nutze das OOPBUY Spreadsheet, um ähnliche Produkte verschiedener Verkäufer zu vergleichen." },
        ].map((item, i) => (
          <li key={i} className="bg-background-100 rounded-xl p-4 border border-foreground-100/30">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-close-line text-red-500 text-xs w-3 h-3 flex items-center justify-center"></i>
              </div>
              <div>
                <p className="text-sm text-foreground-800 font-medium">{item.fehler}</p>
                <p className="text-xs text-primary-700 mt-1"><i className="ri-check-line inline w-3 h-3"></i> {item.loesung}</p>
              </div>
            </div>
          </li>
        ))}
      </ul>

      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">
        Wichtige Begriffe für OOPBUY Anfänger
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 my-6">
        {[
          { begriff: "Spreadsheet", erkl: "Die zentrale Produktliste mit allen Kategorien" },
          { begriff: "QC-Bilder", erkl: "Qualitätskontrollfotos vor dem Versand" },
          { begriff: "Agent", erkl: "Vermittler zwischen dir und dem Verkäufer" },
          { begriff: "Tracking", erkl: "Sendungsverfolgung deiner Bestellung" },
          { begriff: "Haul", erkl: "Eine Sammelbestellung mit mehreren Produkten" },
          { begriff: "W2C", erkl: "Where to Cop — Link zum Produkt" },
        ].map((item, i) => (
          <div key={i} className="bg-background-100 rounded-lg p-3 border border-foreground-100/30">
            <span className="text-sm font-semibold text-foreground-900">{item.begriff}</span>
            <p className="text-xs text-foreground-600 mt-0.5">{item.erkl}</p>
          </div>
        ))}
      </div>

      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">
        Häufige Fragen von Anfängern
      </h2>
      <div className="space-y-4 my-6">
        {faqs.map((faq, i) => (
          <div key={i} className="bg-background-100 rounded-xl p-5 border border-foreground-100/30">
            <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">{faq.q}</h3>
            <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
          </div>
        ))}
      </div>

      <div className="mt-10 pt-8 border-t border-foreground-200">
        <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-4">Weiterführende Ressourcen</h3>
        <div className="flex flex-wrap gap-3">
          <Link to="/" className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-50 text-primary-700 rounded-full text-sm font-medium hover:bg-primary-100 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-home-line w-4 h-4 flex items-center justify-center"></i>
            Zur Startseite
          </Link>
          <Link to="/einkaufsguide" className="inline-flex items-center gap-1.5 px-4 py-2 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-shopping-bag-line w-4 h-4 flex items-center justify-center"></i>
            Einkaufsguide
          </Link>
        </div>
      </div>
    </ArticleLayout>
  );
}